import React, { useState, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { transcriptChunksOf } from './citationTokens';

/**
 * Viewport-aware tooltip rendered via portal so it's never clipped by overflow parents.
 * Measures the anchor button position and clamps the tooltip within viewport edges.
 */
function CitationTooltip({ anchorRef, visible, children, width = 320 }) {
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!visible || !anchorRef?.current) {
      setPos(null);
      return;
    }
    const rect = anchorRef.current.getBoundingClientRect();
    const pad = 8;
    const halfW = width / 2;
    const anchorCenterX = rect.left + rect.width / 2;

    let left = anchorCenterX - halfW;
    let arrowLeft = halfW;

    if (left < pad) {
      arrowLeft = anchorCenterX - pad;
      left = pad;
    } else if (left + width > window.innerWidth - pad) {
      left = window.innerWidth - pad - width;
      arrowLeft = anchorCenterX - left;
    }

    arrowLeft = Math.max(12, Math.min(arrowLeft, width - 12));

    setPos({
      top: rect.top - 8,
      left,
      arrowLeft: `${arrowLeft}px`,
    });
  }, [visible, anchorRef, width]);

  if (!visible || !pos) return null;

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        zIndex: 99999,
        width: `${width}px`,
        top: `${pos.top}px`,
        left: `${pos.left}px`,
        transform: 'translateY(-100%)',
        pointerEvents: 'none',
      }}
      className="bg-gray-900 text-white text-xs rounded-lg shadow-lg p-3"
    >
      {children}
      <div
        className="absolute"
        style={{ top: '100%', left: pos.arrowLeft, transform: 'translateX(-50%)', marginTop: '-1px' }}
      >
        <div className="w-2 h-2 bg-gray-900 rotate-45 transform"></div>
      </div>
    </div>,
    document.body
  );
}

/** [Sn]: a small badge that opens the source; the tooltip shows its title and snippet. */
export function SourceCitation({ num, reference }) {
  const [hovered, setHovered] = useState(false);
  const buttonRef = useRef(null);
  return (
    <span className="inline">
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.preventDefault();
          if (reference && reference.url) {
            window.open(reference.url, '_blank', 'noopener,noreferrer');
          }
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="inline-flex items-center justify-center text-[10px] font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded px-1 py-0 mx-0.5 hover:bg-teal-100 hover:text-teal-800 hover:border-teal-300 transition-all cursor-pointer align-super leading-none"
        style={{ fontSize: '10px', lineHeight: '14px', minWidth: '22px', verticalAlign: 'super' }}
      >
        S{num}
      </button>
      <CitationTooltip anchorRef={buttonRef} visible={hovered && !!reference}>
        <div className="font-semibold text-teal-300 mb-1">[S{num}] {reference?.title}</div>
        {reference?.snippet && <div className="text-gray-300 leading-relaxed italic mb-1">{reference.snippet.substring(0, 200)}{reference.snippet.length > 200 ? '...' : ''}</div>}
        {reference?.url && <div className="text-teal-400 text-[10px] truncate">&#x1F517; {reference.url}</div>}
      </CitationTooltip>
    </span>
  );
}

const sentenceOf = (ref) => ref.sentence || ref.text_preview || '';

const ExcerptLine = ({ reference, maxLen = 100 }) => {
  if (!reference) return null;
  return (
    <div className="text-gray-300 leading-relaxed italic mb-1">
      <span className="text-primary-400 font-medium">{reference.speaker}</span>
      {reference.start ? <span className="text-gray-400"> ({reference.start})</span> : null}
      : &ldquo;{sentenceOf(reference).substring(0, maxLen)}
      {sentenceOf(reference).length > maxLen ? '...' : ''}&rdquo;
    </div>
  );
};

const plural = (count) => (count !== 1 ? 's' : '');

/**
 * What the tooltip of a transcript citation says: one excerpt, the first and last of a
 * consecutive run, or up to four of scattered ones. Null when nothing resolves, so a
 * number the model invented keeps its marker but opens no empty box.
 */
function transcriptTooltipContent(token, chunks, transcriptReferences) {
  if (!transcriptReferences) return null;
  const { start, end } = token;

  if (chunks.length === 1) {
    const ref = transcriptReferences[String(start)];
    if (!ref) return null;
    return (
      <>
        <div className="font-semibold text-primary-300 mb-1">
          {ref.speaker}{ref.start ? <> &middot; {ref.start}</> : null}
        </div>
        <div className="text-gray-300 leading-relaxed italic">&ldquo;{ref.sentence || ref.text_preview}&rdquo;</div>
      </>
    );
  }

  if (chunks.length > 0 && end - start + 1 === chunks.length) {
    const firstRef = transcriptReferences[String(start)];
    const lastRef = transcriptReferences[String(end)];
    if (!firstRef && !lastRef) return null;
    const count = chunks.length;
    return (
      <>
        <div className="font-semibold text-primary-300 mb-1">
          {count} consecutive excerpt{plural(count)}
        </div>
        <ExcerptLine reference={firstRef} />
        {count > 2 && (
          <div className="text-gray-500 text-[10px] mb-1">
            ... {count - 2} more excerpt{count - 2 > 1 ? 's' : ''} ...
          </div>
        )}
        {lastRef && start !== end && <ExcerptLine reference={lastRef} />}
      </>
    );
  }

  const preview = chunks.slice(0, 4);
  return (
    <>
      <div className="font-semibold text-primary-300 mb-1">
        {chunks.length} transcript excerpt{plural(chunks.length)}
      </div>
      {preview.map((n) => {
        const ref = transcriptReferences[String(n)];
        if (!ref) return null;
        return <React.Fragment key={n}><ExcerptLine reference={ref} maxLen={80} /></React.Fragment>;
      })}
      {chunks.length > preview.length && (
        <div className="text-gray-500 text-[10px]">... {chunks.length - preview.length} more</div>
      )}
    </>
  );
}

/** [N] / [N-M] / a run of them: one icon that jumps to the transcript, with excerpts on hover. */
export function TranscriptCitation({ token, transcriptReferences, onCitationClick }) {
  const [hovered, setHovered] = useState(false);
  const buttonRef = useRef(null);
  const chunks = token.chunks || transcriptChunksOf(token);
  const label = chunks.length === 1 ? 'View transcript excerpt' : `View ${chunks.length} transcript excerpts`;
  const tooltipContent = transcriptTooltipContent(token, chunks, transcriptReferences);

  return (
    <span className="inline">
      <button
        type="button"
        ref={buttonRef}
        onClick={(e) => {
          e.preventDefault();
          if (onCitationClick) onCitationClick(token.start, token.end, chunks);
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-label={label}
        title={label}
        className="inline-flex items-center justify-center text-primary-600 hover:text-primary-800 mx-0.5 p-0.5 rounded hover:bg-primary-50 transition-colors cursor-pointer align-baseline"
      >
        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden="true" />
      </button>
      <CitationTooltip anchorRef={buttonRef} visible={hovered && !!tooltipContent}>
        {tooltipContent}
      </CitationTooltip>
    </span>
  );
}
